import React from 'react';
import { trainerRoleLabel } from './trainerUtils';

/**
 * One UPCOMING training session, as a row.
 *
 * Shared by the Overview "Upcoming training" card and the My Records Sessions
 * tab — the two must describe the same session the same
 * way, and the Overview row links into My Records for the fuller picture.
 *
 * Richer than a generic list row: the date reads as a calendar chip so the
 * list scans down the left edge, and the meta line answers when / who / where.
 * Trainer and venue are both nullable in the data model (no trainer assigned;
 * a NULL venue = off-site, online or not recorded), so the row says so in
 * words rather than dropping the field and leaving the reader unsure whether
 * it is missing or just not rendered.
 */

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Hours read '3' not '3.0', but '1.5' keeps its half.
export const fmtHours = (n) => (Number.isInteger(n) ? String(n) : Number(n).toFixed(1));

// Today as a local ISO day — session dates are plain 'YYYY-MM-DD' calendar
// days, so compare as strings and never round-trip through Date (timezone).
export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const dayAfter = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  const next = new Date(y, m - 1, d + 1);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`;
};

// The when / who / where line under a row's title — plain text joined by
// middots. Each item is { text, muted, warn }; falsy items are skipped, so
// callers can write `cond && { ... }` inline.
export const MetaLine = ({ items, className = 'mt-1' }) => {
  const shown = items.filter(Boolean);
  if (shown.length === 0) return null;
  return (
    <div className={`${className} flex flex-wrap items-center gap-y-0.5 text-xs`}>
      {shown.map((it, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="mx-1.5 text-slate-300">·</span>}
          <span className={it.warn ? 'text-amber-700 font-medium' : it.muted ? 'text-slate-500' : 'text-slate-600'}>
            {it.text}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

// Tone classes are spelled out, never interpolated — Tailwind only ships the
// classes it can see in the source, so `bg-${tone}-50` would compile to nothing.
const CHIP_TONES = {
  violet: { box: 'bg-violet-50 border-violet-100', month: 'text-violet-500', day: 'text-violet-700', year: 'text-violet-500' },
  slate: { box: 'bg-slate-50 border-slate-200', month: 'text-slate-500', day: 'text-slate-600', year: 'text-slate-500' },
  emerald: { box: 'bg-emerald-50 border-emerald-100', month: 'text-emerald-500', day: 'text-emerald-700', year: 'text-emerald-500' },
  amber: { box: 'bg-amber-50 border-amber-100', month: 'text-amber-500', day: 'text-amber-700', year: 'text-amber-500' },
};

export const DateChip = ({ date, today, tone = 'violet' }) => {
  const [yyyy, mm, dd] = (date || '').split('-');
  const t = CHIP_TONES[tone] || CHIP_TONES.violet;
  // The year only earns chip space when it isn't the current one — otherwise
  // every row would repeat it.
  const otherYear = yyyy && today && yyyy !== today.slice(0, 4) ? yyyy : null;
  return (
    <div className={`shrink-0 w-11 rounded-lg border py-1 text-center ${t.box}`}>
      <p className={`text-[10px] font-semibold uppercase tracking-wide ${t.month}`}>
        {MONTH_ABBR[Number(mm) - 1] || mm}
      </p>
      <p className={`text-base font-bold leading-tight tabular-nums ${t.day}`}>{dd}</p>
      {otherYear && (
        <p className={`text-[10px] leading-tight tabular-nums ${t.year}`}>{otherYear}</p>
      )}
    </div>
  );
};

const SessionRow = ({ session, today, tomorrow, highlighted }) => {
  const when = session.date === today ? 'Today' : session.date === tomorrow ? 'Tomorrow' : null;
  return (
    <div className={`flex items-start gap-3 px-5 py-3 ${highlighted ? 'bg-violet-50/60 ring-1 ring-inset ring-violet-200' : ''}`}>
      <DateChip date={session.date} today={today} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-slate-800 truncate">{session.courseTitle}</p>
          {when && (
            <span className="shrink-0 px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[11px] font-semibold">
              {when}
            </span>
          )}
        </div>
        <MetaLine items={[
          session.startEndTime && { text: session.startEndTime },
          { text: session.trainerNames || 'Trainer not assigned', muted: !session.trainerNames },
          { text: session.venueName || 'Venue not specified', muted: !session.venueName },
        ]} />
      </div>
      {session.hours != null && (
        <span className="shrink-0 mt-0.5 px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-xs font-medium tabular-nums">
          {fmtHours(session.hours)} h
        </span>
      )}
    </div>
  );
};

/**
 * One session I am booked to TEACH, as a row — shared by the Overview
 * "My Teaching" card and the My Records Teaching panel. Beside the facts it
 * says what the trainer still owes the session: materials put up, quiz
 * written, recording added. Whatever is missing is listed in amber, so the
 * trainer sees the to-do at a glance; a completed session reads grey.
 */
export const TeachingRow = ({ session, today, tomorrow, highlighted }) => {
  const when = session.date === today ? 'Today' : session.date === tomorrow ? 'Tomorrow' : null;
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
  // What the trainer has put up, then what is still missing — the missing
  // list reads amber while the session is open, grey once it is completed.
  // The recording is made after the session, so it is only asked for once
  // the day has come (or once there is one).
  const added = [];
  const missing = [];
  if (session.fileCount > 0) added.push(plural(session.fileCount, 'material')); else missing.push('materials');
  // L&D decides on the Sessions form whether there is a quiz: 'trainer' =
  // the trainers owe it; 'none' = nothing to add; 'bank' always has one.
  if (session.quizId) added.push(`Quiz: ${session.quizTitle}`);
  else if (session.quizMode === 'trainer') missing.push('quiz');
  if (session.recordingCount > 0) added.push(plural(session.recordingCount, 'recording'));
  else if (today && session.date <= today) missing.push('recording');
  return (
    <div className={`flex items-start gap-3 px-5 py-3 ${highlighted ? 'bg-emerald-50/60 ring-1 ring-inset ring-emerald-200' : ''}`}>
      <DateChip date={session.date} today={today} tone={session.completed ? 'slate' : 'emerald'} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium text-slate-800 truncate">{session.courseTitle}</p>
          <span className="shrink-0 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[11px] font-semibold">
            {trainerRoleLabel(session.role)}
          </span>
          {when && !session.completed && (
            <span className="shrink-0 px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[11px] font-semibold">{when}</span>
          )}
          {session.completed && (
            <span className="shrink-0 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[11px] font-semibold">
              Completed
            </span>
          )}
        </div>
        <MetaLine items={[
          session.startEndTime && { text: session.startEndTime },
          { text: session.venueName || 'Venue not specified', muted: !session.venueName },
          session.coTrainerNames && { text: `with ${session.coTrainerNames}` },
          session.expectedCount != null && { text: `${session.expectedCount} expected` },
        ]} />
        <MetaLine className="mt-0.5" items={[
          ...added.map((text) => ({ text })),
          missing.length > 0 && {
            text: `${session.completed ? 'Not added' : 'Still to add'}: ${missing.join(', ')}`,
            warn: !session.completed,
            muted: session.completed,
          },
        ]} />
      </div>
      {session.hours != null && (
        <span className="shrink-0 mt-0.5 px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-xs font-medium tabular-nums">
          {fmtHours(session.hours)} h
        </span>
      )}
    </div>
  );
};

export default SessionRow;
